<template>
  <v-card outlined class="mx-auto mt-4">
    <v-card-subtitle class="pb-4">
      <v-row align="center" no-gutters>
        <v-col cols="12" sm="auto" class="mb-2 mb-sm-0">
          <div class="d-flex align-center font-weight-bold">
            {{ $vuetify.lang.t("$vuetify.conversation_insight.title") }}
            <v-tooltip top max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-on="on" v-bind="attrs" class="ml-1">
                  mdi-help-circle-outline
                </v-icon>
              </template>
              <span>
                {{ $vuetify.lang.t("$vuetify.conversation_insight.tooltip") }}
              </span>
            </v-tooltip>
          </div>
        </v-col>
        <v-spacer class="hidden-xs-only" />
        <v-col cols="12" sm="4" class="mb-2 mb-sm-0">
          <v-autocomplete
            v-model="selectedProfileId"
            :items="gptProfiles"
            item-text="name"
            item-value="id"
            :label="$vuetify.lang.t('$vuetify.conversation_insight.profile_name_label')"
            outlined
            dense
            :disabled="isInsightsLoading || isReindexLoading || isReindexRequired"
            hide-details
            class="mr-sm-2"
            :menu-props="{ 
              zIndex: 3
            }"
          />
        </v-col>
      </v-row>
    </v-card-subtitle>

    <v-divider />

    <div v-if="!hasProfilesLoaded" class="pa-4">
      <v-skeleton-loader type="card-heading" />
    </div>

    <v-alert v-else-if="hasProfilesLoaded && !gptProfiles.length" type="warning" dense outlined prominent text border="left" class="ma-4">
      {{ $vuetify.lang.t("$vuetify.conversation_insight.no_profiles_available") }}
    </v-alert>

    <!-- Reindex Required State -->
    <v-sheet v-if="isReindexRequired" class="pa-6 text-center d-flex flex-column align-center justify-center" min-height="400">
      <div class="text-h6 mb-2">
        {{ $vuetify.lang.t("$vuetify.conversation_insight.project_needs_reindex") }}
        <p v-if="!isReindexLoading" class="text-subtitle-1">{{ $vuetify.lang.t("$vuetify.conversation_insight.reindex_prompt") }}</p>
      </div>
      
      <v-alert v-if="reindexError" type="error" dense outlined class="mb-4" style="max-width: 600px;">
        {{ reindexError }}
      </v-alert>

      <v-btn
        color="primary"
        :loading="isReindexLoading"
        :disabled="isReindexLoading"
        @click="startReindex"
      >
        <!-- <v-icon left>mdi-refresh</v-icon> -->
        {{ $vuetify.lang.t("$vuetify.conversation_insight.start_reindex_btn") }}
      </v-btn>
      <div v-if="isReindexLoading || reindexMessage" class="text-body-2 grey--text mt-2">
        {{ $vuetify.lang.t("$vuetify.conversation_insight.reindex_in_progress") }}
      </div>
    </v-sheet>

    <v-alert v-else-if="insightsStatus === 'error'" type="error" dense class="ma-4">
      {{ errorMessage || $vuetify.lang.t("$vuetify.conversation_insight.load_error") }}
    </v-alert>

    <!-- Content -->
    <v-row v-if="!isReindexRequired" class="pa-6">
      <!-- Top Queries -->
      <v-col cols="12" class="pa-2 d-flex">
        <v-card outlined class="flex-grow-1">
          <v-card-subtitle class="pb-4">
            <v-row align="center" no-gutters>
              <v-col cols="12" sm="auto" class="mb-2 mb-sm-0">
                <div class="d-flex align-center font-weight-bold">
                  <span>{{ $vuetify.lang.t("$vuetify.conversation_insight.top_queries") }}</span>
                  <v-tooltip top max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small v-on="on" v-bind="attrs" class="ml-1">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>
                      {{ $vuetify.lang.t("$vuetify.conversation_insight.top_queries_tooltip") }}
                    </span>
                  </v-tooltip>
                </div>
              </v-col>
              <v-col>
                <v-chip cols="12" sm="4" class="ml-sm-2 mb-2 mb-sm-0" small outlined>
                  {{ $vuetify.lang.t("$vuetify.conversation_insight.total_queries_label") }}
                  <b class="ml-1">{{ totalQueryCount }}</b>
                </v-chip>
              </v-col>
              <v-spacer class="hidden-xs-only" />
              <v-col cols="12" sm="4" class="mb-2 mb-sm-0">
                <v-text-field
                  v-model="topQuerySearch"
                  :label="$vuetify.lang.t('$vuetify.conversation_insight.search_query_label')"
                  outlined
                  clearable
                  dense
                  hide-details
                  class="mr-sm-2"
                  :disabled="isInsightsLoading"
                />
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model.number="numTopics"
                  :label="$vuetify.lang.t('$vuetify.conversation_insight.number_of_topics_label')"
                  type="number"
                  min="1"
                  max="100"
                  outlined
                  dense
                  hide-details
                  :disabled="isInsightsLoading"
                  @keydown="preventInvalidKeys"
                  @input="clampValue('numTopics', $event)"
                  @keyup.enter="autoFetchIfReady"
                  @blur="autoFetchIfReady"
                />
              </v-col>
            </v-row>
          </v-card-subtitle>
          <v-divider />

          <div v-if="isInsightsLoading" class="pa-4">
            <v-skeleton-loader type="table" />
          </div>

          <div v-else-if="!topTopics.length" class="pa-6 text-center">
            <div>{{ $vuetify.lang.t("$vuetify.conversation_insight.no_topic_data") }}</div>
          </div>

          <v-data-table
            v-else
            :headers="topicHeaders"
            :items="filteredTopTopics"
            :items-per-page="topicsPerPage"
            :page="topicPage"
            :expanded.sync="expandedTopics"
            show-expand
            single-expand
            item-key="query"
            :footer-props="{ showFirstLastPage: true }"
            @update:page="topicPage = $event"
            @update:items-per-page="topicsPerPage = $event"
          >
            <template v-slot:[`item.query`]="{ item }">
              <span :title="item.query || ''">
                {{ truncateText(item.query, 90) }}
              </span>
            </template>

            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-4">
                <div class="text-body-2">
                  <strong>{{ $vuetify.lang.t("$vuetify.conversation_insight.full_query") }}:</strong> {{ item.query }}
                </div>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Top Keywords -->
      <v-col cols="12" class="pa-2 d-flex">
        <v-card outlined class="flex-grow-1">
          <v-card-subtitle class="pb-4">
            <v-row align="center" no-gutters>
              <v-col cols="12" sm="auto" class="mb-2 mb-sm-0">
                <div class="d-flex align-center font-weight-bold">
                  <span>{{ $vuetify.lang.t("$vuetify.conversation_insight.top_keywords") }}</span>
                  <v-tooltip top max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small v-on="on" v-bind="attrs" class="ml-1">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>
                      {{ $vuetify.lang.t("$vuetify.conversation_insight.top_keywords_tooltip") }}
                    </span>
                  </v-tooltip>
                </div>
              </v-col>
              <v-spacer />
              <v-col cols="12" sm="2" class="mb-2 mb-sm-0">
                <v-text-field
                  v-model.number="numKeywords"
                  :label="$vuetify.lang.t('$vuetify.conversation_insight.number_of_keywords_label')"
                  type="number"
                  min="1"
                  max="100"
                  outlined
                  dense
                  hide-details
                  :disabled="isInsightsLoading"
                  @keydown="preventInvalidKeys"
                  @input="clampValue('numKeywords', $event)"
                  @keyup.enter="autoFetchIfReady"
                  @blur="autoFetchIfReady"
                />
              </v-col>
            </v-row>
          </v-card-subtitle>
          <v-divider />

          <div v-if="isInsightsLoading" class="pa-4">
            <v-skeleton-loader type="table" />
          </div>

          <div v-else-if="!topKeywords.length" class="pa-6 text-center">
            <div>{{ $vuetify.lang.t("$vuetify.conversation_insight.no_keyword_data") }}</div>
          </div>

          <v-data-table
            v-else
            :headers="keywordHeaders"
            :items="topKeywords"
            :items-per-page="keywordsPerPage"
            :page="keywordPage"
            :footer-props="{ showFirstLastPage: true }"
            @update:page="keywordPage = $event"
            @update:items-per-page="keywordsPerPage = $event"
          />
        </v-card>
      </v-col>

      <!-- Summary -->
      <v-col cols="12" class="pa-2">
        <v-card outlined class="flex-grow-1">
          <v-card-subtitle class="pb-4">
            <v-row align="center" no-gutters>
              <v-col cols="12" sm="auto" class="mb-2 mb-sm-0">
                <div class="d-flex align-center font-weight-bold">
                  {{ $vuetify.lang.t("$vuetify.conversation_insight.summary_title") }}
                  <v-tooltip top max-width="350">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small v-on="on" v-bind="attrs" class="ml-1">
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>
                      {{ $vuetify.lang.t("$vuetify.conversation_insight.tooltip") }}
                    </span>
                  </v-tooltip>
                </div>
              </v-col>
              <v-spacer />
              <v-col cols="12" sm="auto" class="mb-2 mb-sm-0">
                <v-select
                  v-model="language"
                  :items="languageOptions"
                  :label="$vuetify.lang.t('$vuetify.conversation_insight.response_language_label')"
                  outlined
                  dense
                  :disabled="isInsightsLoading"
                  hide-details
                  :menu-props="{ 
                    zIndex: 3
                  }"
                />
              </v-col>
            </v-row>
          </v-card-subtitle>
          <v-divider />
          <div v-if="isInsightsLoading" class="pa-4">
            <v-skeleton-loader type="paragraph" />
            <v-skeleton-loader type="paragraph" />
          </div>

          <div v-else class="pa-4 summary-box">
            <div
              class="pa-6"
              :class="summaryAlignmentClass"
              v-html="renderMarkdown(insightsSummary)"
            />
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import getEnv from '@/config/env';
import {
  GET_GPT_SETTINGS,
  GET_TASK_STATUS,
  FETCH_PROFILE_CONVERSATION_INSIGHTS,
  REINDEX_PROJECT,
} from '@/store/_actiontypes';
import { buildGptConversationReindexPayload } from '@/global/_constants';

export default {
  name: 'ConversationInsight',

  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
    gptProfiles: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedProfileId: null,
      defaultProfileId: null,
      numTopics: 10,
      numKeywords: 10,
      language: 'en',
      languageOptions: [
        { text: 'English', value: 'en' },
        { text: 'Japanese', value: 'ja' },
        { text: 'Korean', value: 'ko' },
      ],
      totalQueryCount: 0,
      topTopics: [],
      topKeywords: [],
      insightsSummary: '',
      insightsStatus: 'idle',
      isInsightsLoading: false,
      errorMessage: '',
      topicHeaders: [
        { text: this.$vuetify.lang.t('$vuetify.conversation_insight.query_header'), value: 'query', sortable: false, width: '68%' },
        { text: this.$vuetify.lang.t('$vuetify.conversation_insight.count_header'), value: 'count', sortable: true, align: 'start', width: '32%' },
      ],
      keywordHeaders: [
        { text: this.$vuetify.lang.t('$vuetify.conversation_insight.keyword_header'), value: 'keyword', sortable: false, width: '69%' },
        { text: this.$vuetify.lang.t('$vuetify.conversation_insight.count_header'), value: 'count', sortable: true, align: 'start', width: '31%' },
      ],
      topicPage: 1,
      keywordPage: 1,
      topicsPerPage: 10,
      keywordsPerPage: 10,
      topQuerySearch: '',
      isReindexRequired: false,
      isReindexLoading: false,
      reindexError: '',
      reindexMessage: '',
      pollingInterval: null,
      taskId: null,
      hasProfilesLoaded: false,
      numTopicsDebounce: null,
      numKeywordsDebounce: null,
      expandedTopics: [],
      isDefaultProfileLoaded: false,
    };
  },

  computed: {
    canFetch() {
      return !!(
        this.projectId &&
        this.selectedProfileId &&
        this.startDate &&
        this.endDate &&
        this.numTopics >= 1 &&
        this.numTopics <= 100 &&
        this.numKeywords >= 1 &&
        this.numKeywords <= 100
      );
    },

    filteredTopTopics() {
      const term = (this.topQuerySearch || '').trim().toLowerCase();
      if (!term) return this.topTopics || [];
      return (this.topTopics || []).filter(item =>
        (item.query || '').toLowerCase().includes(term)
      );
    },

    hasInsightData() {
      return this.topTopics.length > 0 || this.topKeywords.length > 0;
    },

    summaryAlignmentClass() {
      return this.insightsSummary ? 'text-left' : 'text-center';
    }
  },

  watch: {
    projectId: {
      immediate: true,
      async handler(projectId) {
        if (projectId) {
          this.isDefaultProfileLoaded = false;
          this.defaultProfileId = null;
          this.selectedProfileId = null;
          await this.fetchDefaultProfile();
        }
      },
    },

    gptProfiles: {
      immediate: true,
      handler(profiles) {
        this.hasProfilesLoaded = true;
        if (profiles?.length) {
          this.applyProfileSelection();
        } else {
          this.selectedProfileId = null;
          this.topTopics = [];
          this.topKeywords = [];
          this.insightsSummary = '';
          this.totalQueryCount = 0;
          this.insightsStatus = 'idle';
        }
      },
    },
    
    defaultProfileId(newVal) {
      if (newVal && this.isDefaultProfileLoaded && this.gptProfiles?.length) {
        this.applyProfileSelection();
      }
    },

    startDate: 'autoFetchIfReady',
    endDate: 'autoFetchIfReady',
    selectedProfileId: 'autoFetchIfReady',
    language: 'autoFetchIfReady',

    topTopics() {
      this.topicPage = 1;
      this.expandedTopics = [];
    },

    topKeywords() {
      this.keywordPage = 1;
      this.expandedTopics = [];
    },

    topQuerySearch() {
      this.topicPage = 1;
    },
  },
  
  beforeDestroy() {
    this.stopReindexInterval();
    this.taskId = null;
    if (this.numTopicsDebounce) clearTimeout(this.numTopicsDebounce);
    if (this.numKeywordsDebounce) clearTimeout(this.numKeywordsDebounce);
  },

  methods: {
    ...mapActions('project', [
      GET_GPT_SETTINGS,
      FETCH_PROFILE_CONVERSATION_INSIGHTS,
      REINDEX_PROJECT,
    ]),
    ...mapActions('task', [GET_TASK_STATUS]),

    renderMarkdown(text) {
      if (!text) {
        return this.$vuetify.lang.t('$vuetify.conversation_insight.no_summary_returned');
      }
      return text
        .replace(/\*\*\*([\s\S]*?)\*\*\*/g, '<strong><em>$1</em></strong>')
        .replace(/\*\*([\s\S]*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([\s\S]*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>');
    },

    async fetchDefaultProfile() {
      if (!this.projectId) return;
      this.isDefaultProfileLoaded = false;
      
      try {
        const response = await this.GET_GPT_SETTINGS({
          project_id: this.projectId,
        });
        this.defaultProfileId = response?.data?.detail?.settings?.default_profile || null;
      } catch (error) {
        console.error(
          this.$vuetify.lang.t('$vuetify.conversation_insight.load_gpt_settings_error'),
          error
        );
        this.defaultProfileId = null;
      } finally {
        this.isDefaultProfileLoaded = true;
        // profile selection after default is loaded
        if (this.gptProfiles?.length) {
          this.applyProfileSelection();
        }
      }
    },

    applyProfileSelection() {
      if (this.selectedProfileId && this.gptProfiles?.some(p => p.id === this.selectedProfileId)) {
        return;
      }

      if (!this.gptProfiles?.length) {
        this.selectedProfileId = null;
        return;
      }

      if (!this.isDefaultProfileLoaded) {
        return;
      }

      // Use default profile if it exists
      if (this.defaultProfileId) {
        const profileExists = this.gptProfiles.some(
          profile => profile.id === this.defaultProfileId
        );
        
        if (profileExists) {
          this.selectedProfileId = this.defaultProfileId;
          this.autoFetchIfReady();
          return;
        }
      }

      // Fallback to last profile
      this.selectedProfileId = this.gptProfiles[this.gptProfiles.length - 1]?.id || null;
      this.autoFetchIfReady();
    },

    preventInvalidKeys(event) {
      const invalidKeys = ['-', '+', 'e', 'E', '.'];
      if (invalidKeys.includes(event.key)) {
        event.preventDefault();
      }
    },

    clampValue(fieldName, value) {
      this.$nextTick(() => {
        const currentValue = this[fieldName];
        
        if (currentValue === '' || currentValue === null) return;
        
        if (currentValue < 1) {
          this[fieldName] = 1;
        } else if (currentValue > 100) {
          this[fieldName] = 100;
        }
      });
    },

    truncateText(text, maxLength = 90) {
      const str = String(text || '');
      return str.length > maxLength ? `${str.slice(0, maxLength)}...` : str;
    },

    autoFetchIfReady() {
      if (this.canFetch && ['idle', 'success'].includes(this.insightsStatus)) {
        this.fetchConversationInsights();
      }
    },

    mapInsightsResponse(response) {
      const detail = response?.data?.detail || {};
      const raw = detail.raw || {};
      const data = raw.data || {};

      return {
        isReindexRequired: raw.is_reindex_required === true,
        taskId: raw.task_id || null,
        totalQueryCount: data.total_queries ?? 0,
        topTopics: data.top_topics ?? [],
        topKeywords: data.top_keywords ?? [],
        insightsSummary: detail.summary ?? '',
      };
    },

    async startReindex() {
      if (!this.projectId) return;

      // If there's already a task running
      if (this.taskId && this.isReindexLoading) {
        return;
      }

      this.isReindexLoading = true;
      this.reindexError = '';
      this.reindexMessage = '';

      try {
        const response = await this.REINDEX_PROJECT({
          project_id: this.projectId,
          payload: buildGptConversationReindexPayload(),
        });

        const taskId = response?.data?.detail?.task_id;
        if (!taskId) {
          this.isReindexRequired = false;
          await this.fetchConversationInsights();
          return;
        }

        this.taskId = taskId;
        this.reindexMessage =
          response?.data?.detail?.response?.message;

        this.startReindexInterval(taskId);
      } catch (error) {
        const errorDetail = error?.response?.data || error;
        this.reindexError = 
          errorDetail?.message || 
          errorDetail?.raw?.message || 
          error?.message
        this.isReindexLoading = false;
      }
    },

    startReindexInterval(taskId) {
      this.stopReindexInterval();
      this.taskId = taskId;
      this.pollingInterval = setInterval(() => {
        this.GET_TASK_STATUS({
          project_id: this.projectId,
          task_id: taskId,
        }).then(
          async response => {
            const taskResponse = response?.data?.detail?.response;
            if (taskResponse?.status === 'success') {
              this.stopReindexInterval();
              this.taskId = null;
              this.isReindexLoading = false;
              this.reindexMessage =
                taskResponse?.message;
              this.isReindexRequired = false;
              await this.fetchConversationInsights();
            }
            if (taskResponse?.status === 'failed') {
              this.stopReindexInterval();
              this.taskId = null;
              this.isReindexLoading = false;
              this.reindexError =
                taskResponse?.message;
            }
          },
          error => {
            this.stopReindexInterval();
            this.taskId = null;
            this.isReindexLoading = false;
            this.reindexError =
              error?.message;
          },
        );
      }, getEnv('VUE_APP_POLLING_INTERVAL'));
    },

    stopReindexInterval() {
      if (!this.pollingInterval) return;
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    },

    async fetchConversationInsights() {
      if (!this.canFetch) return;

      this.isInsightsLoading = true;
      this.insightsStatus = 'loading';
      this.errorMessage = '';

      try {
        const response = await this.FETCH_PROFILE_CONVERSATION_INSIGHTS({
          project_id: this.projectId,
          maira_profile_id: this.selectedProfileId,
          start_date: this.startDate,
          end_date: this.endDate,
          top_n_topics: this.numTopics,
          top_n_keywords: this.numKeywords,
          response_language: this.language,
        });

        const normalized = this.mapInsightsResponse(response);

        if (normalized.isReindexRequired) {
          this.isReindexRequired = true;
          this.taskId = normalized.taskId;
          this.insightsStatus = 'idle';
          
          // If there's an existing task
          if (this.taskId) {
            this.isReindexLoading = true;
            // this.reindexMessage = this.$vuetify.lang.t('$vuetify.conversation_insight.reindex_in_progress');
            this.startReindexInterval(this.taskId);
          }
          
          return;
        }

        this.isReindexRequired = false;
        this.totalQueryCount = normalized.totalQueryCount;
        this.topTopics = normalized.topTopics;
        this.topKeywords = normalized.topKeywords;
        this.insightsSummary = normalized.insightsSummary;
        this.insightsStatus = 'success';
      } catch (error) {
        this.insightsStatus = 'error';
        this.errorMessage = 
          error?.message || 
          this.$vuetify.lang.t('$vuetify.conversation_insight.load_error');
      } finally {
        this.isInsightsLoading = false;
      }
    },
    
  },
};
</script>

<style scoped>
.summary-box {
  white-space: pre-wrap;
  line-height: 1.5;
}
</style>
